import React, { useEffect, useState } from 'react';
import { GoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import GoogleSignUp from './GoogleSignUp';

const AuthLanding = ({ setPageToggle, GradeWizHeader }) => {
  const navigate = useNavigate();
  const { googleLogin, checkGoogleAccount } = useAuth();
  const [showGoogleSignUp, setShowGoogleSignUp] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);
  const [googleUserData, setGoogleUserData] = useState(null);
  const [error, setError] = useState(null);

  // Handle credentials from both regular button and One Tap UI
  const handleGoogleCredential = async (credential) => {
    try {
      setError(null);
      const rawToken = credential;

      // Check if the account exists first
      const accountCheck = await checkGoogleAccount(rawToken);

      if (accountCheck.error) {
        setError(accountCheck.error);
        return;
      }

      // If the account exists, proceed with login
      if (accountCheck.exists) {
        const result = await googleLogin(rawToken);

        if (typeof result === 'string' || result instanceof String || result.error) {
          // Show error
          setError(typeof result === 'string' ? result : result.error);
          return;
        }

        // Navigate based on user role
        if (result.user.is_verified) {
          if (result.user.is_admin) {
            return navigate('/admin');
          } else if (result.user.is_teacher) {
            return navigate('/tclasses');
          }
          return navigate('/sclasses');
        }
      } else {
        // Account doesn't exist, show sign-up form
        setGoogleToken(rawToken);
        // Extract user data from the token
        const decodedToken = jwtDecode(rawToken);
        setGoogleUserData({
          email: decodedToken.email,
          first_name: decodedToken.given_name || '',
          last_name: decodedToken.family_name || ''
        });
        setShowGoogleSignUp(true);
      }
    } catch (err) {
      console.error('Error during Google authentication:', err);
      setError('An error occurred during authentication. Please try again.');
    }
  };

  // Set up One Tap login
  useGoogleOneTapLogin({
    onSuccess: credentialResponse => {
      handleGoogleCredential(credentialResponse.credential);
    },
    onError: () => {
      console.log('One Tap Login Failed');
      setError('Google sign-in failed. Please try again or use the button below.');
    }
  });

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    /* Style the Google button container */
    .google-button-container > div {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    }
    
    /* Style the button itself */
    .google-button-container div[role="button"] {
      background-color: #ffffff !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
    
    /* Hover effect matching your email button */
    .google-button-container div[role="button"]:hover {
      background-color: #fef9f4 !important;
      border-color: #c99b69 !important;
      box-shadow: 0 8px 24px rgba(228, 177, 122, 0.25) !important;
      transform: translateY(-2px) !important;
    }
    
    /* Active/pressed state */
    .google-button-container div[role="button"]:active {
      transform: translateY(0) !important;
      box-shadow: 0 4px 12px rgba(228, 177, 122, 0.15) !important;
    }
    
    /* Style the text inside the button */
    .google-button-container span {
      color: #333333 !important;
      font-weight: 500 !important;
    }
  `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleGoogleSuccess = async credentialResponse => {
    handleGoogleCredential(credentialResponse.credential);
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };

  const handleContinueWithEmail = () => {
    setPageToggle('sign-in');
  };

  const handleBackFromGoogleSignUp = () => {
    setShowGoogleSignUp(false);
    setGoogleToken(null);
    setGoogleUserData(null);
  };

  // Show Google SignUp form if needed
  if (showGoogleSignUp && googleToken && googleUserData) {
    return (
      <GoogleSignUp
        googleToken={googleToken}
        userData={googleUserData}
        onBackClick={handleBackFromGoogleSignUp}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-7">
        <GradeWizHeader />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-8">Sign in to GradeWiz</h1>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm max-w-xs">
            {error}
          </div>
        )}

        <div className="w-full max-w-xs space-y-5">
          {/* Google login button */}
          <div className="flex justify-center google-button-container">
            <GoogleLogin
              useOneTap={false}
              theme="outline"
              shape="rectangular"
              text="continue_with"
              locale="en_US"
              width="300"
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
            />
          </div>

          {/* Or divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or</span>
            </div>
          </div>

          {/* Email login button styled like finalize button */}
          <button
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-md font-medium transition-all duration-300 text-white"
            style={{
              background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
              boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
            }}
            onClick={handleContinueWithEmail}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(228, 177, 122, 0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(228, 177, 122, 0.2)';
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <span className="text-base">Continue with Email</span>
          </button>

          <div className="mt-3 text-center text-xs text-gray-500">
            If you are using a school email, please continue with email since your IT might block
            third-party apps.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLanding;
